import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import Lenis from "lenis";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, Zap, Bot, Tag, Flame, Twitter, ShieldCheck, ArrowRight, Lock, Check, X,
  Radar, Swords, CircleDollarSign, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";

const EASE = [0.16, 1, 0.3, 1];

const TICKER = [
  "TARGET — POKEMON 151 RESTOCK", "WALMART — EARLY MONITOR HIT", "POKEMON CENTER — ETB LIVE",
  "AMAZON — PRICE ERROR -73%", "P-BANDAI — DROP DETECTED", "LORCANA — TARGET 1PLUS STOCK",
  "TOPPS CHROME — RESTOCK PING", "AMAZON WAREHOUSE — FLIP ALERT", "COSTCO — POKEMON TIN BUNDLE",
  "BEST BUY — SPORTS CARDS LIVE",
];

const FEED = [
  { t: "00:00:04", c: "#target-1plus-stock", m: "Pokemon 151 Booster Bundle — IN STOCK" },
  { t: "00:00:11", c: "#pokemon-center", m: "Prismatic Evolutions ETB — QUEUE LIVE" },
  { t: "00:00:19", c: "#amazon-price-errors", m: "ETB listed at $11.20 — ERROR -78%" },
  { t: "00:00:26", c: "#walmart-early-monitor", m: "Destined Rivals restock detected" },
  { t: "00:00:33", c: "#stellar-checkout-feed", m: "14 checkouts confirmed" },
  { t: "00:00:41", c: "#target-deals", m: "Flip: $34 → $61 clearance find" },
];

const MONITORS = {
  pokemon: {
    label: "Pokemon",
    icon: Radar,
    desc: "Manual & early-warning coverage across every major Pokemon retailer.",
    channels: ["Target — Pokemon Manual", "Target — 1Plus Stock", "Target Early Monitor", "Walmart Early Monitor", "Pokemon Center", "Amazon", "Walmart", "GT Collectibles", "Costco", "Sam's Club", "Crunchyroll", "Menards"],
  },
  tcg: {
    label: "Other TCGs",
    icon: Sparkles,
    desc: "One Piece, MTG, Lorcana, Riftbound & more — never miss a print run.",
    channels: ["Target — One Piece Manual", "Target — OP 1Plus Stock", "Target — MTG", "Target — Sports Cards", "Target — Riftbound", "Target — Lorcana", "Ravensburger US", "P-Bandai", "Riftbound"],
  },
  sports: {
    label: "Sports",
    icon: Swords,
    desc: "Topps, Chrome & hobby retail restocks across big-box stores.",
    channels: ["Topps", "Best Buy Sports", "GT Collectibles Sports"],
  },
  clearance: {
    label: "Clearance & Errors",
    icon: Tag,
    desc: "Price errors and hidden clearance before anyone else sees them.",
    channels: ["Home Depot", "Bass Pro Shop", "Best Buy", "Amazon Price Errors", "Amazon 90–100% Off", "Amazon 80–90% Off", "Amazon 70–80% Off", "Amazon Warehouse"],
  },
  flips: {
    label: "Flips",
    icon: Flame,
    desc: "Retail arbitrage and resale deals across every category.",
    channels: ["Needoh Monitors", "Target Deals", "Electronic Deals", "Woot Deals", "Gymshark", "Macy's Deals", "Apple"],
  },
  bothub: {
    label: "Bot Hub",
    icon: Bot,
    desc: "Live checkout feeds from the top bots running in the server.",
    channels: ["Stellar Checkout Feed", "Hayha Checkout Feed", "Refract Checkout Feed", "Polaraio Checkout Feed", "Valor Checkout Feed", "Poke Bot Checkouts", "Aggregated Checkout Feed", "Bot Update Feed"],
  },
};

const FREE_FEATURES = [
  { ok: true, t: "Server announcements & drop news" },
  { ok: true, t: "Giveaways & community chat" },
  { ok: true, t: "Limited restock pings" },
  { ok: true, t: "A taste of the Cartel before you commit" },
  { ok: false, t: "40+ full-speed monitors" },
  { ok: false, t: "Price errors & clearance alerts" },
  { ok: false, t: "Bot checkout feeds" },
];

const PAID_FEATURES = [
  "40+ Pokemon, TCG, sports & clearance monitors",
  "Amazon price errors & warehouse flips",
  "Bot checkout feeds (Stellar, Hayha, Refract & more)",
  "Instant TCG-pings & flip-pings",
  "Target leads & early stock warnings",
  "Flip deals: electronics, apparel, Apple & more",
  "Online / in-store / bot success channels",
  "Ticket support from the Cartel team",
  "Affiliate program — earn on every referral",
];

const FAQ = [
  { q: "What exactly is Card Cartel?", a: "A private Discord cook group for TCG collectors and resellers. We run real-time monitors across Target, Walmart, Pokemon Center, Amazon, Costco and more — plus price errors, clearance leads, bot checkout feeds and flip deals." },
  { q: "What is the free Waiting Room?", a: "A limited free section of our Discord. You get announcements, giveaways, community chat and a few sample pings — enough to see the quality before upgrading to the full syndicate." },
  { q: "How do I get access after paying?", a: "Once your $15.99/mo payment confirms, your member dashboard instantly unlocks the private Discord invite. One click and you're inside — no waiting on staff." },
  { q: "Can I cancel anytime?", a: "Yes. Manage or cancel your subscription anytime from your dashboard through the Stripe billing portal. No contracts, no exit fees." },
  { q: "How does the affiliate program work?", a: "Every member gets a unique referral link. Share it anywhere — when someone signs up and subscribes through your link, you earn a commission on their membership. Track clicks, signups and earnings from your dashboard." },
];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Section({ children, className = "", id }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, ease: EASE }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [settings, setSettings] = useState({ free_discord_invite: "", commission_percent: 20, price: 15.99 });
  const [tab, setTab] = useState("pokemon");
  const [faqOpen, setFaqOpen] = useState(0);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const emblemX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const emblemY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const feedX = useTransform(sx, [-0.5, 0.5], [10, -10]);
  const feedY = useTransform(sy, [-0.5, 0.5], [8, -8]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      localStorage.setItem("cc_ref", ref);
      api.post("/affiliate/click", { code: ref }).catch(() => {});
    }
  }, [searchParams]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const openWaitingRoom = () => {
    if (settings.free_discord_invite) {
      window.open(settings.free_discord_invite, "_blank", "noopener");
    } else {
      toast.info("The waiting room link is being finalized — check back shortly.");
    }
  };

  const startCheckout = async () => {
    if (!user) {
      navigate("/login?intent=checkout");
      return;
    }
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout — try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-[rgba(212,175,55,0.15)]">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Card Cartel home">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-8 mono text-[11px] tracking-[0.2em] uppercase text-zinc-400">
            <button data-testid="nav-monitors-link" onClick={() => scrollTo("monitors")} className="hover:text-[#E6C665] transition-colors">Monitors</button>
            <button data-testid="nav-pricing-link" onClick={() => scrollTo("pricing")} className="hover:text-[#E6C665] transition-colors">Pricing</button>
            <button data-testid="nav-faq-link" onClick={() => scrollTo("faq")} className="hover:text-[#E6C665] transition-colors">FAQ</button>
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-free-discord-btn"
              onClick={openWaitingRoom}
              className="btn-ghost-gold hidden sm:block text-xs font-sub font-semibold px-4 py-2.5 rounded-full"
            >
              Free Waiting Room
            </button>
            <button
              data-testid="nav-login-btn"
              onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="btn-gold text-xs font-sub px-5 py-2.5 rounded-full"
            >
              {user ? "Dashboard" : "Member Login"}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative pt-[72px] hero-glow"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width - 0.5);
          my.set((e.clientY - r.top) / r.height - 0.5);
        }}
      >
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 gold-ring rounded-full px-4 py-2 mb-8"
            >
              <span className="live-dot w-2 h-2 rounded-full bg-[#D4AF37]" />
              <span className="mono text-[10px] tracking-[0.25em] uppercase text-[#E6C665]">Monitors live — 40+ channels watching</span>
            </motion.div>

            <h1 data-testid="hero-headline" className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              <Reveal delay={0.1}>OWN EVERY</Reveal>
              <Reveal delay={0.24}><span className="gold-text">DROP.</span></Reveal>
              <Reveal delay={0.38}>SKIP THE L'S.</Reveal>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
            >
              Card Cartel is the underground network for TCG collectors and resellers — real-time
              Target, Walmart &amp; Pokemon Center monitors, Amazon price errors, bot checkout feeds
              and flip deals. One membership. No Whop fees.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-join-paid"
                onClick={startCheckout}
                className="btn-gold flex items-center gap-2 font-sub text-sm px-7 py-4 rounded-full"
              >
                Join the Syndicate — $15.99/mo <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-free-waiting-room"
                onClick={openWaitingRoom}
                className="btn-ghost-gold flex items-center gap-2 font-sub font-semibold text-sm px-7 py-4 rounded-full"
              >
                <Crown className="w-4 h-4" /> Free Waiting Room
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-10 flex items-center gap-6 mono text-[10px] tracking-[0.2em] uppercase text-zinc-600"
            >
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#8A7322]" /> Stripe secured</span>
              <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#8A7322]" /> Instant access</span>
              <span className="flex items-center gap-1.5"><X className="w-3.5 h-3.5 text-[#8A7322]" /> Cancel anytime</span>
            </motion.div>
          </div>

          {/* Hero visual: coin + live feed */}
          <div className="relative hidden lg:block h-[520px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
              style={{ x: emblemX, y: emblemY }}
              className="absolute top-4 right-8 w-64 h-64"
            >
              <div className="relative w-full h-full">
                <div className="absolute -inset-4 rounded-full animate-spin-slow" style={{ border: "1px dashed rgba(212,175,55,0.35)" }} />
                <img
                  src="/logo.png"
                  alt="Card Cartel emblem"
                  className="w-full h-full rounded-full object-cover"
                  style={{ boxShadow: "0 0 70px rgba(212,175,55,0.4)" }}
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: EASE }}
              style={{ x: feedX, y: feedY }}
              className="absolute bottom-0 left-0 w-[380px] glass gold-ring rounded-xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="mono text-[10px] tracking-[0.25em] uppercase text-[#E6C665]">Live Monitor Stream</span>
                <span className="flex items-center gap-1.5 mono text-[10px] text-zinc-500">
                  <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> LIVE
                </span>
              </div>
              <div className="space-y-3">
                {FEED.map((f, i) => (
                  <motion.div
                    key={f.c + f.t}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + i * 0.16, ease: EASE }}
                    className="flex items-start gap-3"
                  >
                    <span className="mono text-[10px] text-zinc-600 pt-0.5 shrink-0">{f.t}</span>
                    <div className="min-w-0">
                      <p className="mono text-[11px] text-[#E6C665] truncate">{f.c}</p>
                      <p className="text-xs text-zinc-400 truncate">{f.m}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="border-y border-[rgba(212,175,55,0.2)] bg-[#0C0C10] py-3.5 overflow-hidden" data-testid="alert-ticker">
        <div className="animate-marquee flex whitespace-nowrap w-max">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mono text-[11px] tracking-[0.18em] text-[#E6C665]/80 uppercase mx-8 flex items-center gap-8">
              {t} <span className="text-[#8A7322]">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* MANIFESTO */}
      <Section className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">The Cartel Code</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
          Why the underground <span className="gold-text">eats first</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {[
            { n: "01", icon: Zap, t: "Speed Is the Only Edge", d: "Retail drops sell out in minutes. Our monitors fire the second stock hits — Target, Walmart, Pokemon Center, Amazon and beyond — so you're checking out while everyone else is still waking up." },
            { n: "02", icon: Radar, t: "Information Is Currency", d: "Price errors, hidden clearance, 1Plus stock, early links, bot checkout feeds. The Cartel aggregates what the public never sees and pipes it straight into one server." },
            { n: "03", icon: Crown, t: "The Cartel Eats Together", d: "Success channels, giveaways, ticket support and an affiliate program that pays you to grow the family. When one of us eats, all of us eat." },
          ].map((c) => (
            <motion.div
              key={c.n}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="card-lift relative bg-[#101014] border border-[#26262F] rounded-2xl p-8"
            >
              <span className="mono text-xs text-[#8A7322] tracking-[0.3em]">{c.n}</span>
              <c.icon className="w-6 h-6 text-[#D4AF37] mt-6 mb-4" />
              <h3 className="font-sub font-bold text-lg mb-3">{c.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* MONITORS */}
      <Section id="monitors" className="bg-[#0C0C10] border-y border-[#26262F]">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">Inside the Server</p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                40+ monitors. <span className="gold-text">Zero mercy.</span>
              </h2>
            </div>
            <p className="text-sm text-zinc-500 max-w-sm">A sample of the channels running around the clock inside Card Cartel.</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {Object.entries(MONITORS).map(([key, val]) => (
              <button
                key={key}
                data-testid={`monitors-tab-${key}`}
                onClick={() => setTab(key)}
                className={`mono text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-full border transition-all duration-300 ${
                  tab === key
                    ? "border-[#D4AF37] text-[#0A0A0C] bg-gradient-to-r from-[#F5D77F] to-[#D4AF37] font-bold"
                    : "border-[#26262F] text-zinc-400 hover:border-[rgba(212,175,55,0.4)] hover:text-[#E6C665]"
                }`}
              >
                {val.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <p className="text-sm text-zinc-500 mb-6 flex items-center gap-2">
                {(() => { const Icon = MONITORS[tab].icon; return <Icon className="w-4 h-4 text-[#D4AF37]" />; })()}
                {MONITORS[tab].desc}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {MONITORS[tab].channels.map((c) => (
                  <div key={c} className="chip rounded-lg px-4 py-3.5 flex items-center gap-3">
                    <span className="mono text-[#8A7322] text-sm">#</span>
                    <span className="text-sm text-zinc-300">{c}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Section>

      {/* FREE VS PAID */}
      <Section className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4 text-center">Choose Your Rank</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center">
          Start free. <span className="gold-text">Ascend when ready.</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6 mt-14 max-w-4xl mx-auto">
          <div className="card-lift bg-[#101014] border border-[#26262F] rounded-2xl p-8">
            <p className="mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Free</p>
            <h3 className="font-display font-bold text-xl mb-1">Waiting Room</h3>
            <p className="font-sub font-bold text-3xl mt-4">$0</p>
            <p className="text-xs text-zinc-500 mb-8">forever</p>
            <ul className="space-y-3.5 mb-10">
              {FREE_FEATURES.map((f) => (
                <li key={f.t} className="flex items-start gap-3 text-sm">
                  {f.ok ? <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> : <X className="w-4 h-4 text-zinc-700 mt-0.5 shrink-0" />}
                  <span className={f.ok ? "text-zinc-300" : "text-zinc-600 line-through"}>{f.t}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-free-waiting-room-btn"
              onClick={openWaitingRoom}
              className="btn-ghost-gold w-full font-sub font-semibold text-sm py-3.5 rounded-full"
            >
              Enter the Waiting Room
            </button>
          </div>

          <div className="card-lift relative bg-[#101014] rounded-2xl p-8 border border-[rgba(212,175,55,0.5)]" style={{ boxShadow: "0 0 60px rgba(212,175,55,0.12)" }}>
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F5D77F] to-[#D4AF37] text-[#0A0A0C] mono text-[10px] tracking-[0.25em] uppercase font-bold px-4 py-1.5 rounded-full">
              Full Syndicate
            </div>
            <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#E6C665] mb-2">Membership</p>
            <h3 className="font-display font-bold text-xl mb-1">Card Cartel</h3>
            <p className="font-sub font-bold text-3xl mt-4">
              $15.99<span className="text-sm text-zinc-500 font-normal">/month</span>
            </p>
            <p className="text-xs text-zinc-500 mb-8">cancel anytime</p>
            <ul className="space-y-3.5 mb-10">
              {PAID_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                  <span className="text-zinc-200">{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-card-checkout-btn"
              onClick={startCheckout}
              className="btn-gold w-full flex items-center justify-center gap-2 font-sub text-sm py-4 rounded-full"
            >
              <Lock className="w-4 h-4" /> Unlock Full Access
            </button>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="bg-[#0C0C10] border-y border-[#26262F]">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">Initiation</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">In the server in 60 seconds</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              { n: "01", t: "Create your account", d: "Sign up with email or one click with Google. Referred by a member? Your link tracks it automatically." },
              { n: "02", t: "Subscribe — $15.99/mo", d: "Secure checkout through Stripe. No Whop fees eating your margin, no middlemen." },
              { n: "03", t: "Unlock the Discord", d: "The moment payment confirms, your private invite appears on the dashboard. Click, join, cook." },
            ].map((s) => (
              <div key={s.n} className="relative pl-8 border-l border-[#26262F]">
                <span className="absolute -left-[9px] top-0 w-4 h-4 rounded-full emblem-coin" />
                <span className="mono text-xs text-[#8A7322] tracking-[0.3em]">{s.n}</span>
                <h3 className="font-sub font-bold text-lg mt-3 mb-2">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* PRICING anchor */}
      <Section id="pricing" className="max-w-7xl mx-auto px-6 py-24 lg:py-32 text-center">
        <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">One Price. Everything.</p>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          <span className="gold-text">$15.99</span>
          <span className="text-2xl sm:text-3xl text-zinc-500 font-bold">/month</span>
        </h2>
        <p className="mt-6 text-base text-zinc-400 max-w-xl mx-auto">
          Every monitor, every feed, every flip. One restock pays for months of membership.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <button
            data-testid="pricing-section-checkout-btn"
            onClick={startCheckout}
            className="btn-gold flex items-center gap-2 font-sub text-sm px-8 py-4 rounded-full"
          >
            Join the Cartel <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={openWaitingRoom} className="btn-ghost-gold font-sub font-semibold text-sm px-8 py-4 rounded-full">
            Try Free First
          </button>
        </div>
        <p className="mt-8 mono text-[10px] tracking-[0.25em] uppercase text-zinc-600 flex items-center justify-center gap-2">
          <CircleDollarSign className="w-3.5 h-3.5 text-[#8A7322]" />
          Affiliates earn {settings.commission_percent}% per paid referral
        </p>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-[#0C0C10] border-y border-[#26262F]">
        <div className="max-w-3xl mx-auto px-6 py-24 lg:py-32">
          <p className="mono text-[11px] tracking-[0.3em] uppercase text-[#8A7322] mb-4 text-center">Intel</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center mb-14">Questions, answered</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={f.q} className="border border-[#26262F] rounded-xl bg-[#101014] overflow-hidden">
                <button
                  data-testid={`faq-item-${i}`}
                  onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-sub font-semibold text-sm sm:text-base">{f.q}</span>
                  <motion.span animate={{ rotate: faqOpen === i ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-[#D4AF37] text-xl font-light shrink-0 ml-4">+</motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {faqOpen === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                    >
                      <p className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <div>
            <Logo />
            <p className="text-xs text-zinc-600 mt-4 max-w-xs leading-relaxed">
              The premier TCG &amp; flip underground network. Monitors, price errors, bot feeds and deals — gold standard only.
            </p>
          </div>
          <div className="flex flex-wrap gap-12">
            <div>
              <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">Cartel</p>
              <div className="space-y-2.5 text-sm text-zinc-400">
                <button onClick={() => scrollTo("monitors")} className="block hover:text-[#E6C665] transition-colors">Monitors</button>
                <button onClick={() => scrollTo("pricing")} className="block hover:text-[#E6C665] transition-colors">Pricing</button>
                <button onClick={() => navigate("/login")} className="block hover:text-[#E6C665] transition-colors">Member Login</button>
              </div>
            </div>
            <div>
              <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] mb-4">Earn With Us</p>
              <div className="space-y-2.5 text-sm text-zinc-400">
                <button data-testid="footer-affiliate-link" onClick={() => navigate(user ? "/dashboard" : "/login")} className="block hover:text-[#E6C665] transition-colors">Affiliate Program</button>
                <button onClick={openWaitingRoom} className="block hover:text-[#E6C665] transition-colors">Free Waiting Room</button>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#26262F] mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">© 2026 Card Cartel. All rights reserved.</p>
          <p className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 flex items-center gap-2">
            <Twitter className="w-3.5 h-3.5" /> Not affiliated with Pokemon, Target, or any retailer
          </p>
        </div>
      </footer>
    </div>
  );
}
